import { css } from '@emotion/css'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Vulnerability } from '../../../../components/shared'
import { FilterTree } from './filter-tree'
import {
  Dependency,
  DependencyTreeResult,
  DependencyType,
  ModulesResponse,
  Report,
  SelectedFilters,
  TreeType
} from './interfaces'
import { TreePresenter } from './tree-presenter'
import { useCurrentOrganization } from 'contrastBase/state'

interface Props {
  reports: Report[]
  report: Report
  setSelectedApplicationId: React.Dispatch<React.SetStateAction<string>>
  selectedApplicationId: string
  treeData: DependencyTreeResult
  setLibraryFilter: React.Dispatch<React.SetStateAction<DependencyType>>
  setSelectedReport: React.Dispatch<React.SetStateAction<Report | undefined>>
  selectedFilters: SelectedFilters
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>
  mergedApplications?: ModulesResponse
  treeType: TreeType
}

export const DependencyTreeView = ({
  reports,
  selectedApplicationId,
  setSelectedApplicationId,
  mergedApplications,
  treeData,
  setLibraryFilter,
  report,
  setSelectedReport,
  selectedFilters,
  setSelectedFilters,
  treeType
}: Props) => {
  const [cliVersion, setCliVersion] = useState(` v${report.cliVersion}`)
  const [allCollapsed, setAllCollapsed] = useState(false)
  const [vulnerableOnly, setVulnerableOnly] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [count, setCount] = useState(0)
  const [vulnerableCount, setVulnerableCount] = useState(0)
  const [cveCount, setCveCount] = useState(0)
  const [treeResult, setTreeResult] = useState<Dependency[]>([])
  const [vulnerable, setVulnerable] = useState<Dependency[]>([])
  const { currentOrganization: organization } = useCurrentOrganization()

  const calculateCount = (dependencies: Dependency[]) => {
    let noOfNodes = 0

    dependencies.forEach((dependency) => {
      noOfNodes++

      noOfNodes += calculateCount(dependency.children)
    })

    return noOfNodes
  }

  const calculateVulnCounts = (
    treeType: TreeType,
    vulnerableLibraryCount: number,
    dependencies: Dependency[]
  ) => {
    const cves = new Set<string>()

    if (treeType === 'LegacyCLI') {
      return { noOfNodes: vulnerableLibraryCount, cves }
    }
    let noOfNodes = 0

    dependencies.forEach((dependency) => {
      noOfNodes++

      dependency.vulnerabilities.forEach((vuln) => cves.add(vuln.name))

      noOfNodes += calculateVulnCounts(
        treeType,
        vulnerableLibraryCount,
        dependency.children.filter((dep) => dep.hasVulnerableDependency)
      ).noOfNodes
    })

    return { noOfNodes, cves }
  }

  const filterVulnerable = (dependencies: Dependency[]) => {
    const vulns: Dependency[] = []

    dependencies.forEach((dependency) => {
      if (
        dependency.hasVulnerableDependency ||
        dependency.vulnerabilities.length > 0
      ) {
        vulns.push(dependency)
      }
    })

    return vulns
  }

  useEffect(() => {
    if (treeData && treeData.vulnerabilities) {
      setTreeResult(treeData.dependencyTree)

      const copiedTree = JSON.parse(JSON.stringify(treeData))

      const vulnerableLibraries = filterVulnerable(copiedTree.dependencyTree)

      setVulnerable(vulnerableLibraries)

      setCount(calculateCount(copiedTree.dependencyTree))

      const vulnerableLibraryCount = Object.keys(
        treeData.vulnerabilities
      ).length

      const vulnCounts = calculateVulnCounts(
        treeType,
        vulnerableLibraryCount,
        vulnerableLibraries
      )

      setVulnerableCount(vulnCounts.noOfNodes)

      let cves: Vulnerability[] = []

      Object.values(treeData.vulnerabilities).forEach((vulns: any) => {
        cves = [...cves, ...vulns]
      })

      setCveCount(treeType === 'LegacyCLI' ? cves.length : vulnCounts.cves.size)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData])

  const isMatch = (libraryName: string, searchString: string) => {
    return libraryName.toLowerCase().includes(searchString)
  }

  useEffect(() => {
    if (searchString.length > 0) {
      setAllCollapsed(true)

      treeData?.dependencyTree.forEach((value) => {
        isMatch(value.name, searchString)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString])

  const handleReportSelection = (reportSelected: Report) => {
    setSelectedReport(reportSelected)

    setCliVersion(` v${reportSelected.cliVersion}`)
  }

  const collapseAll = () => {
    setAllCollapsed(!allCollapsed)
  }

  const setVulnerableStatus = (vulnerableStatus: boolean) => {
    setVulnerableOnly(vulnerableStatus)
  }

  const filter = (e: string) => {
    setSearchString(e.toLowerCase())
  }

  return (
    <>
      <>
        <FilterTree
          setCollapsedExpanded={collapseAll}
          setSelectedApplicationId={setSelectedApplicationId}
          allCollapsed={allCollapsed}
          setVulnerableStatus={setVulnerableStatus}
          filterTree={filter}
          totalCount={count}
          vulnerableCount={vulnerableCount}
          report={report}
          reports={reports}
          handleReportSelection={handleReportSelection}
          cliVersion={cliVersion}
          setLibraryFilter={setLibraryFilter}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          mergedApplications={mergedApplications}
          selectedApplicationId={selectedApplicationId}
          treeType={treeType}
        />

        <strong
          data-testid="vulnerable-cve-count-display"
          className={css`
            margin-left: 15px;

            margin-bottom: 10px;
          `}
        >
          <FormattedMessage
            id="title_applications_dependencyTreeVulnerableCount"
            values={{ vulnerableCount, cveCount }}
          />
        </strong>
      </>

      {vulnerable && vulnerable.length > 0 && vulnerableOnly && (
        <TreePresenter
          itemSize={30}
          treeResult={vulnerable}
          allCollapsed={allCollapsed}
          searchString={searchString}
          organization={organization}
          treeType={treeType}
        />
      )}

      {treeResult && treeResult.length > 0 && !vulnerableOnly && (
        <TreePresenter
          itemSize={30}
          treeResult={treeResult}
          allCollapsed={allCollapsed}
          searchString={searchString}
          organization={organization}
          treeType={treeType}
        />
      )}
    </>
  )
}
